<script setup lang="ts">
import { ref, watch } from 'vue'
import MarkdownIt from 'markdown-it'
import { useHelpStore } from '@/stores/useHelpStore'

const helpStore = useHelpStore()
const renderedHtml = ref('')

const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true
})

watch(
  () => helpStore.currentContent,
  (content) => {
    if (content) {
      renderedHtml.value = md.render(content)
    } else {
      renderedHtml.value = ''
    }
  },
  { immediate: true }
)

function handleContentClick(event: MouseEvent) {
  const target = event.target as HTMLElement
  const anchor = target.closest('a')
  if (!anchor) return

  const href = anchor.getAttribute('href')
  if (!href || !href.startsWith('/help/')) return

  // Internal help link: extract route from filename
  event.preventDefault()
  const filename = href.replace('/help/', '').replace('.md', '')
  const page = helpStore.helpIndex?.pages.find(p => p.file.replace('.md', '') === filename)
  if (page) {
    helpStore.navigateTo(page.route)
  }
}
</script>

<template>
  <!-- Loading state -->
  <div v-if="helpStore.isLoading" class="help-loading">
    <svg class="spinner" viewBox="0 0 24 24" fill="none">
      <circle class="spinner-track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
      <path class="spinner-head" d="M12 2a10 10 0 0 1 10 10" stroke="#6F3B94" stroke-width="3" stroke-linecap="round" />
    </svg>
    <p class="loading-text">Chargement de l'aide...</p>
  </div>

  <!-- Fallback: no content -->
  <div v-else-if="!helpStore.currentContent" class="help-fallback">
    <svg class="fallback-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
      <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
    </svg>
    <p class="fallback-text">Aide non disponible pour cette page.</p>
  </div>

  <!-- Rendered markdown content -->
  <div v-else class="help-content" v-html="renderedHtml" @click="handleContentClick" />
</template>

<style scoped>
.help-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

.spinner-track {
  opacity: 0.2;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: #6b7280;
  font-size: 0.875rem;
}

.help-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 12px;
}

.fallback-icon {
  width: 48px;
  height: 48px;
  color: #9ca3af;
}

.fallback-text {
  color: #6b7280;
  font-size: 1rem;
}

/* Markdown rendered content styles */
.help-content :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  color: #212529;
  margin-bottom: 1rem;
}

.help-content :deep(h2) {
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 0.75rem;
  margin-top: 1.5rem;
}

.help-content :deep(h3) {
  font-size: 1.125rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
}

.help-content :deep(p) {
  font-size: 1rem;
  color: #374151;
  margin-bottom: 0.75rem;
  line-height: 1.625;
}

.help-content :deep(img) {
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 1rem 0;
  border: 1px solid #e5e7eb;
}

.help-content :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.help-content :deep(ol li) {
  margin-bottom: 0.5rem;
}

.help-content :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.help-content :deep(ul li) {
  margin-bottom: 0.5rem;
}

.help-content :deep(strong) {
  font-weight: 600;
  color: #212529;
}

.help-content :deep(code) {
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.help-content :deep(blockquote) {
  border-left: 3px solid #6F3B94;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #4b5563;
  font-style: italic;
}

.help-content :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1.5rem 0;
}

.help-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.help-content :deep(th),
.help-content :deep(td) {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.help-content :deep(th) {
  background: #f9fafb;
  font-weight: 600;
}
</style>
